import { z } from 'zod';

import type { Utils } from '@cadstrom/sigma-engine-shared';

export type SnapshotRequest = {
	requestId: string;
};

const SnapshotRequestSchema = z.object({
	requestId: z.string(),
}) satisfies Utils.Zot<SnapshotRequest>;

export type VersionedSnapshot = {
	updatedAt: number;
	sourceWindowId: string;
};

export function parseSnapshotRequest(value: unknown): SnapshotRequest | null {
	const result = SnapshotRequestSchema.safeParse(value);
	return result.success ? result.data : null;
}

export function compareVersion(a: VersionedSnapshot, b: VersionedSnapshot): number {
	if (a.updatedAt !== b.updatedAt) {
		return a.updatedAt - b.updatedAt;
	}
	return a.sourceWindowId.localeCompare(b.sourceWindowId);
}

export function pickLatest<T extends VersionedSnapshot>(values: readonly T[]): T | null {
	return values.reduce<T | null>((latest, value) => {
		if (!latest || compareVersion(value, latest) > 0) {
			return value;
		}
		return latest;
	}, null);
}

export function pickLatestByKey<T extends VersionedSnapshot>(values: readonly T[], getKey: (value: T) => string): T[] {
	const latest = new Map<string, T>();
	for (const value of values) {
		const key = getKey(value);
		const previous = latest.get(key);
		if (!previous || compareVersion(value, previous) > 0) {
			latest.set(key, value);
		}
	}
	return [...latest.values()];
}

export function createRequestId(namespace: string): string {
	return `${namespace}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Builds a schema that parses an array, keeping only the elements that match `schema` and silently
 * dropping any that do not, rather than rejecting the whole array when a single element is invalid.
 *
 * @param schema - The element schema each entry is validated against.
 * @returns A schema whose output is an array of the valid, parsed elements.
 */
export function arrayKeepingValid<TOutput>(schema: z.ZodType<TOutput, unknown>): z.ZodType<TOutput[], unknown> {
	return z.array(z.unknown()).transform((entries) =>
		entries.flatMap((entry): TOutput[] => {
			const result = schema.safeParse(entry);
			return result.success ? [result.data] : [];
		}),
	);
}
